import DealDetail from "./Deal";
import DealContactsTable from "../contacts/DealContactsTable";
import DealAccordionsTable from "./DealAccordionsTable";
import { useParams, useSearchParams } from "react-router-dom";
import {
  useEffect,
  useState,
  useRef,
  type FC,
  useMemo,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import type { Asset, Deal, Accordion } from "../../types/deal";
import {
  apiGetById,
  apiGetByIdFile,
  apiGet,
  apiPut,
} from "../../services/genesisApi/httpsMethods";
import { RightSideBar } from "../../components/layout/RightSideBar";
import { useGlobalProvider } from "../../context/GlobalContext";
import { useCurrentUser } from "../../context/CurrentUserContext";
import {
  DealSidePanel,
  type DealSidePanelTab,
} from "../../components/deal/DealSidePanel";
import { useNotifications } from "../../context/NotificationsContext";
import Tenants from "../tenant/Tenants";
import type { MenuDropDownItem } from "../../components/common/MenuDropDown";
import { LoadingBackdrop } from "../../components/common/LoadingBackdrop";
import SendToHawkDialog from "../../components/deal/SendToHawkDialog";
import type { HawkAction } from "../../components/deal/SendToHawkDialog";
import { useHawkFlow } from "../../hooks/useHawkFlow";
import DealAdvisorFees from "../../components/deal/DealAdvisorFees";
import TenPagerUploadDialog from "./components/TenPagerUploadDialog";
import DealGalleryTab from "./components/DealGalleryTab";
import DealNdaTab from "./components/DealNdaTab";
import {
  DealDetailToolbar,
  type DealDetailTabId,
} from "./components/DealDetailToolbar";
import { DealBoxFilesTab } from "./components/DealBoxFilesTab";
import { DealAssetsTab } from "./components/DealAssetsTab";
import { DealSponsorsTab } from "./components/DealSponsorsTab";
import { DealSummaryEditDialog } from "./components/DealSummaryEditDialog";
import { DealMapFullscreenDialog } from "./components/DealMapFullscreenDialog";
import { useDealImages } from "../../hooks/useDealImages";
import DealStatusHistorySection from "./DealStatusHistorySection";
import { fetchDealTemplateVariants } from "../addTemplates/dealTemplateApi";
import {
  getDealHistory,
  type DealHistoryEntry,
} from "../../services/dealHistoryApi";
import type { MenuDropDownSubMenuItem } from "../../components/common/MenuDropDown";
import DealActivityDrawer from "./components/DealActivityDrawer";
import styles from "@/assets/css/Deals.module.css";
import "@/assets/css/tabs.css";
import DealCompsSection from "@/components/deal/DealCompSection";
import {
  AutoFixHigh,
  DescriptionOutlined,
  ShowChartOutlined,
} from "@mui/icons-material";
import doveIcon from "@/assets/svg/dove.svg";
import { useTenantFeatures } from "@/context/TenantFeaturesContext";
import { ErrorDialog } from "@/components/common/errorDialog";
import { useErrorDialog } from "@/hooks/useErrorDialog";

interface DealWrapperProps {
  id?: number;
}

function toDownloadDealName(dealName: string | null | undefined): string {
  const trimmed = dealName?.trim();
  if (!trimmed) return "Deal";
  return trimmed.replace(/[<>:"/\\|?*]/g, "_");
}

const DealWrapper: FC<DealWrapperProps> = ({ id }) => {
  const { isFeatureEnabled, showBoxFiles } = useTenantFeatures();
  const { canEdit } = useCurrentUser();
  const params = useParams<{ dealId?: string }>();
  const dealId = id ?? (params.dealId ? parseInt(params.dealId) : undefined);
  const [deal, setDeal] = useState<Deal | null>(null);
  const dealNameRef = useRef("Deal");
  dealNameRef.current = toDownloadDealName(deal?.deal_name);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [accordions, setAccordions] = useState<Accordion[]>([]);
  const [dealHistory, setDealHistory] = useState<DealHistoryEntry[]>([]);
  const [dealHistoryLoading, setDealHistoryLoading] = useState(false);
  const [activityDrawerOpen, setActivityDrawerOpen] = useState(false);
  const dealRef = useRef<HTMLDivElement>(null);
  const statusHistoryRef = useRef<HTMLDivElement>(null);
  const dealCompsRef = useRef<HTMLDivElement>(null);
  const sponsorsRef = useRef<HTMLDivElement>(null);
  const contactsRef = useRef<HTMLDivElement>(null);
  const accordionsRef = useRef<HTMLDivElement>(null);
  const assetsRef = useRef<HTMLDivElement>(null);
  const tenantsRef = useRef<HTMLDivElement>(null);
  const filesRef = useRef<HTMLDivElement>(null);
  const advisorFeesRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { setRightSideBarOpen } = useGlobalProvider();
  const { markReadByEntity } = useNotifications();
  const [sidePanelTab, setSidePanelTab] = useState<DealSidePanelTab>("notes");
  const [highlightTaskId, setHighlightTaskId] = useState<number | null>(null);
  const [emailSummaryDialogOpen, setEmailSummaryDialogOpen] = useState(false);
  const [emailSummaryContent, setEmailSummaryContent] = useState("");
  const [emailSummaryLoading, setEmailSummaryLoading] = useState(false);
  const [emailSummarySaving, setEmailSummarySaving] = useState(false);
  const [memoExecutiveSummaryDialogOpen, setMemoExecutiveSummaryDialogOpen] =
    useState(false);
  const [memoExecutiveSummaryContent, setMemoExecutiveSummaryContent] =
    useState("");
  const [memoExecutiveSummaryLoading, setMemoExecutiveSummaryLoading] =
    useState(false);
  const [memoExecutiveSummarySaving, setMemoExecutiveSummarySaving] =
    useState(false);
  const [mapFullscreenOpen, setMapFullscreenOpen] = useState(false);
  const [tenPagerUploadOpen, setTenPagerUploadOpen] = useState(false);
  const [boxIframeKey, setBoxIframeKey] = useState(0);
  const [selectedBoxFolderKey, setSelectedBoxFolderKey] = useState("deal");
  const [tenPagerVariants, setTenPagerVariants] = useState<
    MenuDropDownSubMenuItem[]
  >([]);
  const [tenPagerLoading, setTenPagerLoading] = useState(false);
  const { errorDialog, showError, showErrorFromResponse, hideError } =
    useErrorDialog();

  useEffect(() => {
    if (!dealId) return;

    const taskIdParam = searchParams.get("taskId");
    const openTasksParam = searchParams.get("tasks");
    const next = new URLSearchParams(searchParams);
    let changed = false;

    if (openTasksParam === "1") {
      setRightSideBarOpen(true);
      setSidePanelTab("tasks");
      next.delete("tasks");
      changed = true;
    }

    if (taskIdParam) {
      const taskId = parseInt(taskIdParam, 10);
      if (!Number.isNaN(taskId)) {
        setRightSideBarOpen(true);
        setSidePanelTab("tasks");
        setHighlightTaskId(taskId);
        void markReadByEntity({ entity_type: "deal_task", entity_id: taskId });
        next.delete("taskId");
        changed = true;
      }
    }

    if (changed) {
      setSearchParams(next, { replace: true });
    }
  }, [
    dealId,
    searchParams,
    setSearchParams,
    setRightSideBarOpen,
    markReadByEntity,
  ]);

  const openNotesPanel = () => {
    setSidePanelTab("notes");
    setRightSideBarOpen(true);
  };

  type DealTabId = DealDetailTabId;

  const DEAL_TAB_IDS: DealTabId[] = [
    "deal",
    "statusHistory",
    "sponsors",
    "contacts",
    "accordions",
    "assets",
    "tenants",
    "files",
    "advisorFees",
    "dealComps",
    "nda",
    "gallery",
  ];

  const resolveDealTab = (param: string | null): DealTabId | null => {
    if (!param) return null;
    const normalized = param.trim().toLowerCase();
    if (
      normalized === "addon" ||
      normalized === "addons" ||
      normalized === "add-ons" ||
      normalized === "add-on"
    ) {
      return "accordions";
    }
    return DEAL_TAB_IDS.includes(param as DealTabId)
      ? (param as DealTabId)
      : null;
  };

  const dealImagesState = useDealImages(dealId);

  const tabNavItems: { id: DealTabId; label: string }[] = [
    { id: "deal", label: "Deal" },
    { id: "statusHistory", label: "Status History" },
    { id: "sponsors", label: "Sponsors" },
    { id: "contacts", label: "Contacts" },
    { id: "accordions", label: "Add-Ons" },
    { id: "assets", label: "Assets" },
    { id: "tenants", label: "Tenants" },
  ];

  if (showBoxFiles) {
    tabNavItems.push({ id: "files", label: "Files" });
  }
  if (isFeatureEnabled("deal_advisor_fees")) {
    tabNavItems.push({ id: "advisorFees", label: "Advisor Fees" });
  }
  tabNavItems.push({ id: "gallery", label: "Photos" });
  tabNavItems.push({ id: "dealComps", label: "Comps" });
  tabNavItems.push({ id: "nda", label: "NDA" });

  const [activeTab, setActiveTab] = useState<DealTabId>(
    () => resolveDealTab(searchParams.get("tab")) ?? "deal",
  );

  useEffect(() => {
    const fromUrl = resolveDealTab(searchParams.get("tab"));
    if (fromUrl) setActiveTab(fromUrl);
  }, [dealId, searchParams]);

  const dealMapInfo = useMemo(
    () => ({
      sectors: deal?.sectors ?? [],
      countries: deal?.countries ?? [],
    }),
    [deal?.sectors, deal?.countries],
  );

  const boxFolderOptions = useMemo(() => {
    const options: {
      key: string;
      label: string;
      folderId: number | null;
    }[] = [
      {
        key: "deal",
        label: deal?.deal_name?.trim() || "Deal",
        folderId:
          typeof deal?.box_folder_id === "number" ? deal.box_folder_id : null,
      },
    ];

    for (const accordion of accordions) {
      options.push({
        key: `addon-${accordion.id}`,
        label: accordion.accordion_name?.trim() || `Addon ${accordion.id}`,
        folderId:
          typeof accordion.box_folder_id === "number"
            ? accordion.box_folder_id
            : null,
      });
    }

    return options;
  }, [deal, accordions]);

  const selectedBoxFolder = useMemo(() => {
    return (
      boxFolderOptions.find((o) => o.key === selectedBoxFolderKey) ??
      boxFolderOptions[0]
    );
  }, [boxFolderOptions, selectedBoxFolderKey]);

  useEffect(() => {
    if (!boxFolderOptions.some((o) => o.key === selectedBoxFolderKey)) {
      setSelectedBoxFolderKey("deal");
    }
  }, [boxFolderOptions, selectedBoxFolderKey]);

  useEffect(() => {
    setBoxIframeKey((k) => k + 1);
  }, [selectedBoxFolderKey]);

  // Fetch deal and assets
  useEffect(() => {
    // Clear the previous deal so /deals/1 -> /deals/2 never shows deal 1's data
    setDeal(null);
    setAssets([]);
    setAccordions([]);
    if (!dealId) return;

    let cancelled = false;

    apiGetById("deal", dealId)
      .then((response) => {
        if (cancelled) return;
        if (response && response.success && response.data) {
          console.log("Deal data:", response.data[0]);
          setDeal(response.data[0]);
        }
      })
      .catch((err) => {
        console.error("Failed to fetch deal:", err);
      });
    apiGet(`assets?dealId=${dealId}`)
      .then((response) => {
        if (cancelled) return;
        if (response && response.success && response.data) {
          setAssets(response.data);
        }
      })
      .catch((err) => {
        console.error("Failed to fetch assets:", err);
      });
    apiGet(`deals/${dealId}/accordions`)
      .then((response) => {
        if (cancelled) return;
        if (response && response.success && response.data) {
          setAccordions(Array.isArray(response.data) ? response.data : []);
        }
      })
      .catch((err) => {
        console.error("Failed to fetch accordions:", err);
      });

    return () => {
      cancelled = true;
    };
  }, [dealId]);

  const hawkEnabled = isFeatureEnabled("hawk_integration");
  const hawk = useHawkFlow({
    dealId,
    enabled: hawkEnabled,
    dealClosed: deal?.status?.is_closed === true,
  });

  // Reload the deal after a Hawk push so hawk_loan_id (stored by the API on
  // push) is current.
  const refreshDealAfterHawkPush = async () => {
    if (!dealId) return;
    const requestedId = dealId;
    const response = await apiGetById("deal", requestedId);
    if (response?.success && response.data?.[0]) {
      const next = response.data[0] as Deal;
      // Ignore the response if the user has moved to another deal meanwhile
      setDeal((prev) => (prev?.id === requestedId ? next : prev));
    } else {
      console.error("Failed to refresh deal after Hawk push:", response?.message);
    }
  };

  const handleHawkChanged = (action: HawkAction) => {
    hawk.reload();
    if (action === "send") void refreshDealAfterHawkPush();
  };

  useEffect(() => {
    setDealHistory([]);
    if (!dealId) {
      setDealHistoryLoading(false);
      return;
    }

    let cancelled = false;
    setDealHistoryLoading(true);

    void getDealHistory(dealId)
      .then((response) => {
        if (cancelled) return;
        if (response.success && Array.isArray(response.data?.history)) {
          setDealHistory(response.data.history);
          return;
        }

        setDealHistory([]);
        if (!response.success) {
          console.error("Failed to fetch deal history:", response.message);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setDealHistory([]);
        console.error("Failed to fetch deal history:", err);
      })
      .finally(() => {
        if (!cancelled) setDealHistoryLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [dealId]);

  const handleTenPagerCreation = async (variant: string) => {
    setTenPagerLoading(true);
    try {
      const queryParams =
        variant && variant !== "default" ? { variant } : undefined;
      const blob = await apiGetByIdFile(
        "generate-ten-pager",
        dealId!,
        queryParams,
      );
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      const dealName = dealNameRef.current;
      const datetime = new Date().toISOString().replace(/[:.]/g, "-");
      a.href = url;
      a.download = `${dealName}_Initial_Materials_${datetime}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error generating ten pager:", error);
      showError(
        undefined,
        "The initial materials could not be generated. Please try again.",
        "Initial Materials Failed",
      );
    } finally {
      setTenPagerLoading(false);
    }
  };

  useEffect(() => {
    if (!dealId) return;
    void fetchDealTemplateVariants("ten_pager").then((variants) => {
      setTenPagerVariants(
        variants.map((variant) => ({
          label: variant.label,
          onClick: () => void handleTenPagerCreation(variant.slug),
        })),
      );
    });
  }, [dealId]);

  const handleApolloEmailDealSummary = async () => {
    setEmailSummaryLoading(true);
    try {
      const response = await apiGet("generate-text-summary", {
        deal_id: dealId!,
        type: "email",
        format: "json",
      });
      if (response && response.success && response.data) {
        setEmailSummaryContent(response.data["content"]);
        setEmailSummaryDialogOpen(true);
      } else {
        showErrorFromResponse(response);
      }
    } catch (error) {
      console.error("Error generating apollo email deal summary:", error);
      showError(
        undefined,
        "The email deal summary could not be generated. Please try again.",
        "Summary Failed",
      );
    } finally {
      setEmailSummaryLoading(false);
    }
  };

  const handleEmailSummarySubmit = async () => {
    setEmailSummarySaving(true);
    try {
      const response = await apiPut("deal", dealId!, {
        email_summary: emailSummaryContent,
      });
      if (response && response.success) {
        console.log("Email summary updated successfully");
        setEmailSummaryDialogOpen(false);
        // Refresh the deal data
        const dealResponse = await apiGetById("deal", dealId!);
        if (dealResponse && dealResponse.success && dealResponse.data) {
          setDeal(dealResponse.data[0]);
        }
        // Reload the page to ensure all components are updated
        window.location.reload();
      } else {
        console.error("Failed to update email summary:", response?.message);
        showErrorFromResponse(response);
      }
    } catch (error) {
      console.error("Error updating email summary:", error);
      showError(
        undefined,
        "The email summary could not be saved. Please try again.",
        "Save Failed",
      );
    } finally {
      setEmailSummarySaving(false);
    }
  };

  const handleEmailSummaryCancel = () => {
    setEmailSummaryDialogOpen(false);
    setEmailSummaryContent("");
  };

  const handleMemoExecutiveSummary = async () => {
    setMemoExecutiveSummaryLoading(true);
    try {
      const response = await apiGet("generate-text-summary", {
        deal_id: dealId!,
        type: "memo",
        format: "json",
      });
      if (response && response.success && response.data) {
        setMemoExecutiveSummaryContent(response.data["content"]);
        setMemoExecutiveSummaryDialogOpen(true);
      } else {
        showErrorFromResponse(response);
      }
    } catch (error) {
      console.error("Error generating memo executive summary:", error);
      showError(
        undefined,
        "The executive summary could not be generated. Please try again.",
        "Summary Failed",
      );
    } finally {
      setMemoExecutiveSummaryLoading(false);
    }
  };

  const handleMemoExecutiveSummarySubmit = async () => {
    setMemoExecutiveSummarySaving(true);
    try {
      const response = await apiPut("deal", dealId!, {
        executive_summary: memoExecutiveSummaryContent,
      });
      if (response && response.success) {
        console.log("Executive summary updated successfully");
        setMemoExecutiveSummaryDialogOpen(false);
        // Refresh the deal data
        const dealResponse = await apiGetById("deal", dealId!);
        if (dealResponse && dealResponse.success && dealResponse.data) {
          setDeal(dealResponse.data[0]);
        }
        // Reload the page to ensure all components are updated
        window.location.reload();
      } else {
        console.error("Failed to update executive summary:", response?.message);
        showErrorFromResponse(response);
      }
    } catch (error) {
      console.error("Error updating executive summary:", error);
      showError(
        undefined,
        "The executive summary could not be saved. Please try again.",
        "Save Failed",
      );
    } finally {
      setMemoExecutiveSummarySaving(false);
    }
  };

  const handleMemoExecutiveSummaryCancel = () => {
    setMemoExecutiveSummaryDialogOpen(false);
    setMemoExecutiveSummaryContent("");
  };

  const dealMenuItems: MenuDropDownItem[] = [
    {
      label: "Create Term Sheet",
      onClick: () => {
        if (!dealId) return;
        navigate(`/deals/${dealId}/create-term-sheet`, {
          state: { deal, assets },
        });
      },
      icon: <DescriptionOutlined fontSize="small" />,
    },
    {
      label: "Create Initial Materials",
      icon: <ShowChartOutlined fontSize="small" />,
      subMenuItems: tenPagerVariants,
    },
    {
      label: "Generate Email Deal Summary",
      onClick: () => handleApolloEmailDealSummary(),
      icon: <AutoFixHigh fontSize="small" />,
    },
    {
      label: "Generate Executive Summary",
      onClick: () => handleMemoExecutiveSummary(),
      icon: <AutoFixHigh fontSize="small" />,
    },
    {
      label: hawk.menuLabel,
      onClick: hawk.openDialog,
      show: hawk.showMenuItem,
      icon: (
        <Box
          component="img"
          src={doveIcon}
          alt="Hawk"
          sx={{ width: 20, height: 20, display: "block" }}
        />
      ),
    },
  ];

  if (!dealId) return <div>Deal ID not found.</div>;

  const tabPanel = (id: DealTabId, children: ReactNode) => (
    <Box
      id={`deal-tabpanel-${id}`}
      role="tabpanel"
      aria-labelledby={`deal-tab-${id}`}
      hidden={activeTab !== id}
      sx={{ pb: 2 }}
    >
      {children}
    </Box>
  );

  return (
    <Box
      sx={{
        position: "relative",
        zIndex: 1,
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        boxSizing: "border-box",
        padding: 0,
      }}
    >
      {deal?.id && (
        <>
          <DealDetailToolbar
            activeTab={activeTab}
            onActiveTabChange={setActiveTab}
            tabNavItems={tabNavItems}
            canEdit={canEdit}
            dealMenuItems={dealMenuItems}
            onOpenNotes={openNotesPanel}
            onOpenActivity={() => setActivityDrawerOpen(true)}
            onEdit={() => {
              if (!canEdit || !dealId) return;
              navigate(`/deals/${dealId}/edit`);
            }}
          />
          <Box sx={{ padding: 2 }}>
            <Box sx={{ zIndex: 100 }}>
              <RightSideBar>
                <DealSidePanel
                  dealId={dealId ?? null}
                  dealName={deal?.deal_name}
                  initialTab={sidePanelTab}
                  highlightTaskId={highlightTaskId}
                  onClose={() => setRightSideBarOpen(false)}
                />
              </RightSideBar>

              {tabPanel(
                "deal",
                <Box ref={dealRef}>
                  <DealDetail
                    dealId={dealId}
                    deal={deal}
                    hero={dealImagesState.hero}
                    images={dealImagesState.images}
                    onOpenGallery={() => setActiveTab("gallery")}
                    onOpenNda={() => {
                      setActiveTab("nda");
                      setSearchParams(
                        (prev) => {
                          const next = new URLSearchParams(prev);
                          next.set("tab", "nda");
                          return next;
                        },
                        { replace: true },
                      );
                    }}
                    onHeroImageError={() => void dealImagesState.refetch()}
                  />
                </Box>,
              )}

              {tabPanel(
                "statusHistory",
                <DealStatusHistorySection
                  sectionRef={statusHistoryRef}
                  dealId={dealId!}
                  height={500}
                  active={activeTab === "statusHistory"}
                />,
              )}

              {tabPanel(
                "sponsors",
                <DealSponsorsTab sponsorsRef={sponsorsRef} deal={deal} />,
              )}

              {tabPanel(
                "contacts",
                <DealContactsTable dealId={dealId} contactsRef={contactsRef} />,
              )}

              {tabPanel(
                "accordions",
                <DealAccordionsTable
                  dealId={dealId}
                  accordionsRef={accordionsRef}
                  accordionsOrginal={accordions}
                />,
              )}

              {tabPanel(
                "assets",
                <DealAssetsTab
                  dealId={dealId}
                  assetsRef={assetsRef}
                  assets={assets}
                  accordions={accordions}
                  dealMapInfo={dealMapInfo}
                  mapFullscreenOpen={mapFullscreenOpen}
                  setMapFullscreenOpen={setMapFullscreenOpen}
                />,
              )}

              {tabPanel(
                "tenants",
                <Box
                  ref={tenantsRef}
                  sx={{
                    mt: 0,
                    height: "calc(100dvh - 100px)",
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Tenants dealId={dealId} />
                </Box>,
              )}

              {showBoxFiles &&
                tabPanel(
                  "files",
                  <DealBoxFilesTab
                    filesRef={filesRef}
                    boxFolderOptions={boxFolderOptions}
                    selectedBoxFolder={selectedBoxFolder}
                    onSelectedBoxFolderKeyChange={setSelectedBoxFolderKey}
                    boxIframeKey={boxIframeKey}
                    onRefreshIframe={() => setBoxIframeKey((k) => k + 1)}
                  />,
                )}

              {isFeatureEnabled("deal_advisor_fees") &&
                tabPanel(
                  "advisorFees",
                  <Box ref={advisorFeesRef} sx={{ mt: 0 }}>
                    <DealAdvisorFees
                      dealId={dealId}
                      accordions={accordions}
                      dealStatus={deal?.status ?? null}
                    />
                  </Box>,
                )}

              {tabPanel(
                "gallery",
                <DealGalleryTab
                  dealId={dealId!}
                  imagesState={dealImagesState}
                />,
              )}

              {tabPanel(
                "dealComps",
                <Box ref={dealCompsRef} sx={{ zIndex: 100 }}>
                  <DealCompsSection
                    dealId={dealId}
                    assets={assets}
                    accordions={accordions}
                    countries={deal?.countries}
                  />
                </Box>,
              )}

              {tabPanel(
                "nda",
                <DealNdaTab
                  deal={deal}
                  onDealUpdated={(next) => setDeal(next)}
                />,
              )}
            </Box>

            <DealMapFullscreenDialog
              open={mapFullscreenOpen}
              onClose={() => setMapFullscreenOpen(false)}
              assets={assets}
              accordions={accordions}
              dealMapInfo={dealMapInfo}
              setMapFullscreenOpen={setMapFullscreenOpen}
            />

            <DealSummaryEditDialog
              open={emailSummaryDialogOpen}
              title="Edit Email Summary"
              value={emailSummaryContent}
              saving={emailSummarySaving}
              onChange={setEmailSummaryContent}
              onCancel={handleEmailSummaryCancel}
              onSave={handleEmailSummarySubmit}
            />

            <DealSummaryEditDialog
              open={memoExecutiveSummaryDialogOpen}
              title="Edit Memo Executive Summary"
              value={memoExecutiveSummaryContent}
              saving={memoExecutiveSummarySaving}
              onChange={setMemoExecutiveSummaryContent}
              onCancel={handleMemoExecutiveSummaryCancel}
              onSave={handleMemoExecutiveSummarySubmit}
            />

            <TenPagerUploadDialog
              open={tenPagerUploadOpen}
              dealId={dealId}
              onClose={() => setTenPagerUploadOpen(false)}
            />

            {hawkEnabled && (
              <SendToHawkDialog
                open={hawk.dialogOpen}
                dealId={dealId}
                status={hawk.status}
                loading={hawk.loading}
                loadError={hawk.error}
                canEdit={canEdit}
                onRetry={hawk.reload}
                onClose={hawk.closeDialog}
                onChanged={handleHawkChanged}
              />
            )}

            <DealActivityDrawer
              open={activityDrawerOpen}
              onClose={() => setActivityDrawerOpen(false)}
              history={dealHistory}
              loading={dealHistoryLoading}
            />

            <ErrorDialog
              open={errorDialog.open}
              onClose={hideError}
              apiResponse={errorDialog.apiResponse}
              customMessage={errorDialog.customMessage}
              title={errorDialog.title}
            />

            <LoadingBackdrop
              open={
                emailSummaryLoading ||
                memoExecutiveSummaryLoading ||
                tenPagerLoading
              }
              message={
                tenPagerLoading
                  ? "Generating initial materials — this may take up to a minute."
                  : undefined
              }
            />
          </Box>
        </>
      )}
      {!deal?.id && (
        <div className={styles.dealsLoading}>
          <CircularProgress size={120} />
        </div>
      )}
    </Box>
  );
};

export default DealWrapper;
